<template>
	<article class="Filters">
		<SelectedFiltersSection>
			<template #header>
				<SelectedFilterSectionHeader />
			</template>
		</SelectedFiltersSection>
		<FilterSelectionAccordion
			v-for="section in visibleFilterSections"
			:key="section.title"
			:title="section.title"
			:filter-types="section.filterTypes"
			:filter-component="section.filterComponent"
		/>
	</article>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue';
import { PortalType } from '@studyportals/domain-client';
import { useSearchStore } from '@stores/useSearchStore';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import FilterSelectionAccordion from './FilterSelectionAccordion/FilterSelectionAccordion.vue';
import SelectedFiltersSection from './SelectedFiltersSection/SelectedFiltersSection.vue';
import SelectedFilterSectionHeader from './SelectedFiltersSection/SelectedFilterSectionHeader/SelectedFilterSectionHeader.vue';
import TuitionFee from './TuitionFee/TuitionFee.vue';
import DurationFilter from './DurationFilter/DurationFilter.vue';
import FormatFilter from './FormatFilter/FormatFilter.vue';
import AttendanceFilter from './AttendanceFilter/AttendanceFilter.vue';
import DegreeFilter from './DegreeFilter/DegreeFilter.vue';
import SpecialProgrammeFilter from './SpecialProgrammeFilter/SpecialProgrammeFilter.vue';
import OrganisationFilter from './OrganisationFilter/OrganisationFilter.vue';
import LocationFilter from './LocationFilter/LocationFilter.vue';
import DisciplineFilter from './DisciplineFilter/DisciplineFilter.vue';
import EducationalFormFilter from './EducationalFormFilter/EducationalFormFilter.vue';
import { FilterProcessingService } from '@app/routing/FilterProcessingService';
import EducationLevelFilter from './EducationLevelFilter/EducationLevelFilter.vue';

type FilterSection = {
	title: string;
	filterTypes: FilterType[];
	filterComponent: Component;
};

const searchStore = useSearchStore();

const isFilterTypeAvailableForPortal = (filterType: FilterType, currentPortalType: PortalType): boolean => {
	if (filterType === FilterType.ORGANISATIONS) {
		// Organisations filter is always visible
		return true;
	}

	const mapper = FilterProcessingService.getInstance().mappers.find((m) => m.filterType === filterType);
	if (!mapper) return true;

	return mapper.getAllExtendedFilterValues(currentPortalType).length > 0;
};

const filterSections: FilterSection[] = [
	{
		title: 'Field of Study',
		filterTypes: [FilterType.DISCIPLINES],
		filterComponent: DisciplineFilter
	},
	{
		title: 'Education Level',
		filterTypes: [FilterType.EDUCATION_LEVEL],
		filterComponent: EducationLevelFilter,
	},
	{
		title: 'Location',
		filterTypes: [FilterType.AREA, FilterType.COUNTRY, FilterType.CONTINENT],
		filterComponent: LocationFilter
	},
	{
		title: 'University',
		filterTypes: [FilterType.ORGANISATIONS],
		filterComponent: OrganisationFilter
	},
	{
		title: 'Tuition Fee',
		filterTypes: [FilterType.TUITION_FEE],
		filterComponent: TuitionFee
	},
	{
		title: 'Educational Form',
		filterTypes: [FilterType.EDUCATIONAL_FORM],
		filterComponent:  EducationalFormFilter
	},
	{
		title: 'Duration',
		filterTypes: [FilterType.DURATION],
		filterComponent: DurationFilter
	},
	{
		title: 'Format',
		filterTypes: [FilterType.DENSITIES],
		filterComponent: FormatFilter
	},
	{
		title: 'Attendance',
		filterTypes: [FilterType.ATTENDANCE],
		filterComponent: AttendanceFilter
	},
	{
		title: 'Degree Type',
		filterTypes: [FilterType.DEGREE_TYPE],
		filterComponent: DegreeFilter
	},
	{
		title: 'Special Programme',
		filterTypes: [FilterType.SPECIAL_PROGRAMMES],
		filterComponent: SpecialProgrammeFilter
	}
];

const visibleFilterSections = computed<FilterSection[]>(() => {
	const currentPortalType = searchStore.portalType as PortalType;

	return filterSections.filter((section) =>
		section.filterTypes.some((filterType) => isFilterTypeAvailableForPortal(filterType, currentPortalType))
	);
});
</script>

<style scoped lang="scss">
	.Filters {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		width: 100%;
		background: $Grey-100;

		@include Breakpoint(Large, ExtraLarge) {
			display: block;
			border-radius: 0.5rem;
			border: 1px solid $Grey-300;
			overflow: hidden;
		}
	}
</style>
